import {
  ArrayInput,
  FileField,
  FileInput,
  Form,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useCanAccess,
  useResourceContext,
} from "react-admin";
import * as React from "react";
import {Button, Divider, Paper, Stack, Typography} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {useFormContext, useWatch} from "react-hook-form";
import {
  CALENDAR_FIRST_DIRECTION,
  FILE_TYPE,
  FileFormValue,
  FolderFormValue,
  MaterialFileResponse,
  PreparedFormFileValue
} from "../FileAttachmentForm/types";
import useFileHandlers from "../FileAttachmentForm/useFileHandlers";
import {ResourceNameEnum} from "types/index";
import {validateSpace} from "utils/index";
import MultiFileAttach from "components/custom/MultiFileAttach";
import {Material} from "components/material/types";

const normalizePathParts = (path?: string | null): string[] => (
  path?.split('/').filter(Boolean).slice(0, 2) || []
);

const getFolderIdByParts = (parts: string[]): string => `folder:/${parts.join('/')}`;

const createFolderId = (): string => `folder-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const validateFolderName = [
  required(),
  validateSpace,
  (value: string) => value?.includes('/') ? 'Название папки не должно содержать /' : undefined,
];

const calendarFirstDirectionChoices = [
  {id: CALENDAR_FIRST_DIRECTION.PATRIOTISM_AND_HISTORICAL_MEMORY, name: 'Патриотизм и историческая память'},
  {id: CALENDAR_FIRST_DIRECTION.EDUCATION_AND_KNOWLEDGE, name: 'Образование и знания'},
  {id: CALENDAR_FIRST_DIRECTION.VOLUNTEERING, name: 'Волонтерство и добровольчество'},
  {id: CALENDAR_FIRST_DIRECTION.CULTURE_AND_ART, name: 'Культура и искусство'},
  {
    id: CALENDAR_FIRST_DIRECTION.DIPLOMACY_AND_INTERNATIONAL_RELATIONS,
    name: 'Дипломатия и международные отношения',
  },
  {id: CALENDAR_FIRST_DIRECTION.MEDIA_AND_COMMUNICATIONS, name: 'Медиа и коммуникации'},
  {id: CALENDAR_FIRST_DIRECTION.COMMUNITY_NATIVE_FAVORITE, name: 'Сообщество «Родные – любимые»'},
  {id: CALENDAR_FIRST_DIRECTION.SPORT, name: 'Спорт'},
  {id: CALENDAR_FIRST_DIRECTION.WORK_PROFESSION_AND_OWN_BUSINESS, name: 'Труд, профессия и свое дело'},
  {id: CALENDAR_FIRST_DIRECTION.SCIENCE_AND_TECHNOLOGY, name: 'Наука и технологии'},
  {id: CALENDAR_FIRST_DIRECTION.TOURISM_AND_TRAVEL, name: 'Туризм и путешествия'},
  {id: CALENDAR_FIRST_DIRECTION.ECOLOGY_AND_NATURE_PROTECTION, name: 'Экология и охрана природы'},
  {id: CALENDAR_FIRST_DIRECTION.HEALTHY_LIFESTYLE, name: 'Здоровый образ жизни'},
];

const getInitialFolders = (files: MaterialFileResponse[] = []): FolderFormValue[] => {
  const foldersMap = new Map<string, FolderFormValue>();

  sortMaterialFiles(files)
    .filter(({fileType}) => fileType !== FILE_TYPE.RECOMMENDATION)
    .forEach(({path}) => {
    const parts = normalizePathParts(path);
    if (!parts.length) return;

    const firstLevelId = getFolderIdByParts([parts[0]]);
    foldersMap.set(firstLevelId, {
      id: firstLevelId,
      name: parts[0],
    });

    if (parts[1]) {
      const secondLevelId = getFolderIdByParts(parts);
      foldersMap.set(secondLevelId, {
        id: secondLevelId,
        name: parts[1],
        parentId: firstLevelId,
      });
    }
    });

  return Array.from(foldersMap.values());
};

const getFolderIdByPath = (path?: string | null): string | undefined => {
  const parts = normalizePathParts(path);
  return parts.length ? getFolderIdByParts(parts) : undefined;
};

const getSortOrder = (order?: number | null): number => (
  order === null || order === undefined ? Number.MAX_SAFE_INTEGER : Number(order)
);

const sortByOrder = <T extends {order?: number | null}>(items: T[]): T[] => (
  items
    .map((item, index) => ({item, index}))
    .sort((a, b) => {
      const orderDiff = getSortOrder(a.item.order) - getSortOrder(b.item.order);
      return orderDiff || a.index - b.index;
    })
    .map(({item}) => item)
);

const sortFiles = (files: PreparedFormFileValue[]): PreparedFormFileValue[] => sortByOrder(files);

const sortMaterialFiles = (files: MaterialFileResponse[]): MaterialFileResponse[] => sortByOrder(files);

const getFileFieldKey = (file: FileFormValue | undefined, index: number): string => (
  `${file?.id || file?.file?.title || file?.name || 'file'}-${index}`
);

const getFolderFieldKey = (folder: FolderFormValue, folders: FolderFormValue[]): string => (
  `${folder.id}-${folders.findIndex(({id}) => id === folder.id)}`
);

const moveArrayItem = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  const updatedItems = [...items];
  [updatedItems[fromIndex], updatedItems[toIndex]] = [updatedItems[toIndex], updatedItems[fromIndex]];
  return updatedItems;
};

interface FileFieldsProps {
  index: number;
  disabled: boolean;
  onDelete: (index: number) => void;
  source?: string;
  onMove?: (index: number, direction: -1 | 1) => void;
  showDirection?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

const FileFields = ({
                      index,
                      disabled,
                      onDelete,
                      source = 'files',
                      onMove,
                      showDirection = false,
                      canMoveUp = false,
                      canMoveDown = false,
                    }: FileFieldsProps) => (
  <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1}}>
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextInput
          source={`${source}.${index}.name`}
          label={'Название документа'}
          size={'small'}
          validate={[required(), validateSpace]}
          disabled={disabled}
          sx={{flex: 1}}
        />
        {onMove && (
          <>
            <Button
              disabled={disabled || !canMoveUp}
              onClick={() => onMove(index, -1)}
              sx={{minWidth: 40, mt: 0.5}}
            >
              <KeyboardArrowUpIcon fontSize="small"/>
            </Button>
            <Button
              disabled={disabled || !canMoveDown}
              onClick={() => onMove(index, 1)}
              sx={{minWidth: 40, mt: 0.5}}
            >
              <KeyboardArrowDownIcon fontSize="small"/>
            </Button>
          </>
        )}
        <Button
          color="error"
          disabled={disabled}
          onClick={() => onDelete(index)}
          sx={{minWidth: 40, mt: 0.5}}
        >
          <DeleteOutlineIcon fontSize="small"/>
        </Button>
      </Stack>
      {showDirection && (
        <SelectInput
          source={`${source}.${index}.direction`}
          label="Направление"
          size="small"
          choices={calendarFirstDirectionChoices}
          disabled={disabled}
        />
      )}
      <FileInput
        source={`${source}.${index}.file`}
        multiple={false}
        label={'Файл'}
        disabled={disabled}
        validate={required()}
        sx={{
          '& .RaFileInput-dropZone': {
            backgroundColor: 'grey.100',
            cursor: 'pointer',
            '& > p': {
              m: 1,
            }
          }
        }}>
        <FileField source="src" title="title" label={'Файл'}/>
      </FileInput>
    </Stack>
  </Paper>
);

interface FolderFilesProps {
  folder: FolderFormValue;
  files: FileFormValue[];
  folders: FolderFormValue[];
  disabled: boolean;
  level?: 1 | 2;
  onAddFolder: (parentId?: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onMoveFolder: (folderId: string, direction: -1 | 1) => void;
  onDeleteFile: (index: number) => void;
  onMoveFile: (index: number, direction: -1 | 1) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

const FolderFiles = ({
                       folder,
                       files,
                       folders,
                       disabled,
                       level = 1,
                       onAddFolder,
                       onDeleteFolder,
                       onMoveFolder,
                       onDeleteFile,
                       onMoveFile,
                       canMoveUp = false,
                       canMoveDown = false,
                     }: FolderFilesProps) => {
  const folderIndex = folders.findIndex(({id}) => id === folder.id);
  const childFolders = folders.filter(({parentId}) => parentId === folder.id);
  const folderFiles = files
    .map((file, index) => ({file, index}))
    .filter(({file}) => file.folderId === folder.id);

  return (
    <Paper variant="outlined" sx={{p: 2, borderRadius: 1, backgroundColor: level === 1 ? 'grey.50' : 'common.white'}}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextInput
            key={`${folder.id}-${folderIndex}`}
            source={`folders.${folderIndex}.name`}
            label={level === 1 ? 'Название папки' : 'Название вложенной папки'}
            size="small"
            validate={validateFolderName}
            disabled={disabled}
            sx={{flex: 1}}
          />
          <Button
            disabled={disabled || !canMoveUp}
            onClick={() => onMoveFolder(folder.id, -1)}
            sx={{minWidth: 40, mt: 0.5}}
          >
            <KeyboardArrowUpIcon fontSize="small"/>
          </Button>
          <Button
            disabled={disabled || !canMoveDown}
            onClick={() => onMoveFolder(folder.id, 1)}
            sx={{minWidth: 40, mt: 0.5}}
          >
            <KeyboardArrowDownIcon fontSize="small"/>
          </Button>
          <Button
            color="error"
            disabled={disabled}
            onClick={() => onDeleteFolder(folder.id)}
            sx={{minWidth: 40, mt: 0.5}}
          >
            <DeleteOutlineIcon fontSize="small"/>
          </Button>
        </Stack>
        <MultiFileAttach
          disabled={disabled}
          label="Добавить файлы в папку"
          getFileData={() => ({folderId: folder.id})}
        />
        {folderFiles.map(({index}, position) => (
          <FileFields
            key={getFileFieldKey(files[index], index)}
            index={index}
            disabled={disabled}
            onDelete={onDeleteFile}
            onMove={onMoveFile}
            showDirection
            canMoveUp={position > 0}
            canMoveDown={position < folderFiles.length - 1}
          />
        ))}
        {level === 1 && (
          <>
            <Button
              startIcon={<CreateNewFolderOutlinedIcon/>}
              disabled={disabled}
              onClick={() => onAddFolder(folder.id)}
              sx={{maxWidth: 'max-content'}}
            >
              Добавить вложенную папку
            </Button>
            {childFolders.map((childFolder, position) => (
              <FolderFiles
                key={getFolderFieldKey(childFolder, folders)}
                folder={childFolder}
                files={files}
                folders={folders}
                disabled={disabled}
                level={2}
                onAddFolder={onAddFolder}
                onDeleteFolder={onDeleteFolder}
                onMoveFolder={onMoveFolder}
                onDeleteFile={onDeleteFile}
                onMoveFile={onMoveFile}
                canMoveUp={position > 0}
                canMoveDown={position < childFolders.length - 1}
              />
            ))}
          </>
        )}
      </Stack>
    </Paper>
  );
};

interface CalendarFilesFieldsProps {
  disabled: boolean;
}

interface RecommendationFilesFieldsProps {
  disabled: boolean;
}

const RecommendationFilesFields = ({disabled}: RecommendationFilesFieldsProps) => {
  const {getValues, setValue} = useFormContext();
  const recommendations = (useWatch({name: 'recommendations'}) || []) as FileFormValue[];

  const handleDeleteRecommendation = (index: number) => {
    const currentRecommendations = getValues('recommendations') || [];
    setValue(
      'recommendations',
      currentRecommendations.filter((_: FileFormValue, fileIndex: number) => fileIndex !== index),
      {
        shouldDirty: true,
        shouldTouch: true,
      }
    );
  };

  const handleMoveRecommendation = (index: number, direction: -1 | 1) => {
    const currentRecommendations: FileFormValue[] = getValues('recommendations') || [];
    const targetIndex = index + direction;

    if (!currentRecommendations[index] || !currentRecommendations[targetIndex]) return;

    setValue('recommendations', moveArrayItem(currentRecommendations, index, targetIndex), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <Stack spacing={2}>
      <Typography>Рекомендации</Typography>
      <MultiFileAttach
        disabled={disabled}
        source="recommendations"
        label="Добавить несколько рекомендаций"
        fileType={FILE_TYPE.RECOMMENDATION}
      />
      {recommendations.map((file, index) => (
        <FileFields
          key={getFileFieldKey(file, index)}
          source="recommendations"
          index={index}
          disabled={disabled}
          onDelete={handleDeleteRecommendation}
          onMove={handleMoveRecommendation}
          canMoveUp={index > 0}
          canMoveDown={index < recommendations.length - 1}
        />
      ))}
    </Stack>
  );
};

const CalendarFilesFields = ({disabled}: CalendarFilesFieldsProps) => {
  const {getValues, setValue} = useFormContext();
  const files = (useWatch({name: 'files'}) || []) as FileFormValue[];
  const folders = (useWatch({name: 'folders'}) || []) as FolderFormValue[];
  const rootFiles = files
    .map((file, index) => ({file, index}))
    .filter(({file}) => !file.folderId);
  const rootFolders = folders.filter(({parentId}) => !parentId);

  const handleAddFolder = (parentId?: string) => {
    const currentFolders = getValues('folders') || [];
    setValue('folders', [...currentFolders, {id: createFolderId(), name: '', parentId}], {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleDeleteFolder = (folderId: string) => {
    const currentFolders: FolderFormValue[] = getValues('folders') || [];
    const folderIdsToDelete = new Set<string>([folderId]);

    currentFolders.forEach((folder) => {
      if (folder.parentId === folderId) {
        folderIdsToDelete.add(folder.id);
      }
    });

    setValue('folders', currentFolders.filter(({id}) => !folderIdsToDelete.has(id)), {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue('files', (getValues('files') || []).filter((file: FileFormValue) => !folderIdsToDelete.has(file.folderId || '')), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleDeleteFile = (index: number) => {
    const currentFiles = getValues('files') || [];
    setValue('files', currentFiles.filter((_: FileFormValue, fileIndex: number) => fileIndex !== index), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleMoveFolder = (folderId: string, direction: -1 | 1) => {
    const currentFolders: FolderFormValue[] = getValues('folders') || [];
    const folder = currentFolders.find(({id}) => id === folderId);
    if (!folder) return;

    const sameLevelFolderIndexes = currentFolders
      .map((currentFolder, folderIndex) => currentFolder.parentId === folder.parentId ? folderIndex : -1)
      .filter((folderIndex) => folderIndex !== -1);
    const folderIndex = currentFolders.findIndex(({id}) => id === folderId);
    const currentPosition = sameLevelFolderIndexes.indexOf(folderIndex);
    const fromIndex = sameLevelFolderIndexes[currentPosition];
    const toIndex = sameLevelFolderIndexes[currentPosition + direction];

    if (fromIndex === undefined || toIndex === undefined) return;

    setValue('folders', moveArrayItem(currentFolders, fromIndex, toIndex), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleMoveFile = (index: number, direction: -1 | 1) => {
    const currentFiles: FileFormValue[] = getValues('files') || [];
    const file = currentFiles[index];
    if (!file) return;

    const sameFolderIndexes = currentFiles
      .map((currentFile, fileIndex) => currentFile.folderId === file.folderId ? fileIndex : -1)
      .filter((fileIndex) => fileIndex !== -1);
    const currentPosition = sameFolderIndexes.indexOf(index);
    const targetIndex = sameFolderIndexes[currentPosition + direction];

    if (targetIndex === undefined) return;

    const updatedFiles = [...currentFiles];
    [updatedFiles[index], updatedFiles[targetIndex]] = [updatedFiles[targetIndex], updatedFiles[index]];

    setValue('files', updatedFiles, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <Stack spacing={2}>
      <MultiFileAttach disabled={disabled}/>
      {rootFiles.map(({index}, position) => (
        <FileFields
          key={getFileFieldKey(files[index], index)}
          index={index}
          disabled={disabled}
          onDelete={handleDeleteFile}
          onMove={handleMoveFile}
          showDirection
          canMoveUp={position > 0}
          canMoveDown={position < rootFiles.length - 1}
        />
      ))}
      {!!rootFolders.length && <Divider/>}
      {rootFolders.map((folder, position) => (
        <FolderFiles
          key={getFolderFieldKey(folder, folders)}
          folder={folder}
          files={files}
          folders={folders}
          disabled={disabled}
          onAddFolder={handleAddFolder}
          onDeleteFolder={handleDeleteFolder}
          onMoveFolder={handleMoveFolder}
          onDeleteFile={handleDeleteFile}
          onMoveFile={handleMoveFile}
          canMoveUp={position > 0}
          canMoveDown={position < rootFolders.length - 1}
        />
      ))}
      <Button
        startIcon={<CreateNewFolderOutlinedIcon/>}
        disabled={disabled}
        onClick={() => handleAddFolder()}
        sx={{maxWidth: 'max-content'}}
      >
        Добавить папку
      </Button>
    </Stack>
  );
};

interface FileAttachmentFormProps {
  material: Material;
  specialView: boolean;
}

const FileAttachmentForm = ({material, specialView}: FileAttachmentFormProps) => {
  const resource = useResourceContext() as ResourceNameEnum.KnowledgeBase || ResourceNameEnum.Material;

  const {canAccess: canUpdate} = useCanAccess({
    resource,
    action: 'update'
  })

  const materialFiles: MaterialFileResponse[] = material?.files || [];
  const isCalendarMode = specialView;

  const prepareFile = (f: MaterialFileResponse): PreparedFormFileValue => ({
    id: f.id,
    name: f.name,
    fileType: f.fileType || FILE_TYPE.REGULAR,
    direction: f.direction,
    path: f.path,
    order: f.order,
    folderId: isCalendarMode && f.fileType !== FILE_TYPE.RECOMMENDATION ? getFolderIdByPath(f.path) : undefined,
    file: {
      title: f.file.name,
      src: f.file.url,
    }
  });

  const recommendationFiles = materialFiles.filter(({fileType}) => fileType === FILE_TYPE.RECOMMENDATION);
  const regularFiles = materialFiles.filter(({fileType}) => fileType !== FILE_TYPE.RECOMMENDATION);

  const preparedFiles = sortFiles(materialFiles.map(prepareFile));
  const recommendations = isCalendarMode
    ? sortFiles(recommendationFiles.map(prepareFile))
    : [];
  const files: PreparedFormFileValue[] = isCalendarMode
    ? sortFiles(regularFiles.map(prepareFile))
    : preparedFiles;

  const folders = isCalendarMode ? getInitialFolders(materialFiles) : [];

  const {loading, handleSubmit} = useFileHandlers(material.id);

  const onSubmit = (data: { files?: FileFormValue[], recommendations?: FileFormValue[], folders?: FolderFormValue[] }) =>
    handleSubmit(data, [...recommendations, ...files]);

  return (
    // @ts-ignore
    <Form
      key={`${material.id}-${isCalendarMode}-${material.updatedAt}`}
      onSubmit={onSubmit}
      record={{id: material.id}}
      defaultValues={{files, recommendations, folders}}
    >
      <Stack spacing={1} p={2}>
        {isCalendarMode && (
          <>
            <RecommendationFilesFields disabled={!canUpdate}/>
            <Divider sx={{my: 1}}/>
          </>
        )}
        <Typography>Файлы</Typography>
        {isCalendarMode ? (
          <CalendarFilesFields disabled={!canUpdate}/>
        ) : (
          <>
            <MultiFileAttach disabled={!canUpdate}/>
            {/* <ArrayInput source="files" label={""} disabled={!canUpdate}> */}
            <ArrayInput source="files" label={""}>
              <SimpleFormIterator
                disabled={!canUpdate}
                sx={{
                  '& .RaSimpleFormIterator-line': {
                    mb: 2.5
                  }
                }}>
                <TextInput source={'name'} label={'Название документа'} size={'small'}
                           validate={[required(), validateSpace]}
                           disabled={!canUpdate}/>
                <FileInput source="file" multiple={false} label={'Файл'}
                           disabled={!canUpdate}
                           validate={required()}
                           sx={{
                             '& .RaFileInput-dropZone': {
                               backgroundColor: 'grey.100',
                               cursor: 'pointer',
                               '& > p': {
                                 m: 1,
                               }
                             }
                           }}>
                  <FileField source="src" title="title" label={'Файл'}/>
                </FileInput>
              </SimpleFormIterator>
            </ArrayInput>
          </>
        )}
        <LoadingButton loading={loading} type={'submit'} variant={'contained'}
                       disabled={!canUpdate}
                       sx={{maxWidth: 'max-content'}}>
          Сохранить
        </LoadingButton>
      </Stack>
    </Form>
  )
}

export default FileAttachmentForm
